import React, { useEffect, useState } from "react";
import {
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  Modal,
  Fade,
  Backdrop,
  IconButton,
  styled,
  InputLabel
} from "@material-ui/core";
import { SnackBar } from "../SnackBar";
import { api } from "../../api";
import { useBillStyles } from "./useBillStyles";
import { BillDetails } from "./BillDetails";
import { Formik, Form, ErrorMessage } from "formik";
import * as yup from "yup";
import { AppLoadingScreen } from "../AppLoadingScreen";
import CancelIcon from "@material-ui/icons/Cancel";
import RecentBillsTable from "./RecentBillsTable";
import LoaderOverlay from "../LoaderOverlay";
import { Helmet } from "react-helmet-async";

export interface IBillDetails {
  billnumber: string;
  billdocnumber: string;
  billterminal: string;
  billamount: string;
  billdate: string;
}

const RedAsteriskInputLabel = styled(InputLabel)(() => ({
  "& .MuiInputLabel-asterisk": {
    color: "red",
  },
}));

export const Bills = () => {
  const classes = useBillStyles();
  const [billNumber, setBillNumber] = useState<string>("");
  const [invalidBillNumber, setInvalidBillNumber] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<boolean>(false);
  const [billDetails, setBillDetails] = useState<Array<object>>([]);
  const [displayBillDetails, setDisplayBillDetails] = useState<boolean>(false);
  const [creditCustomerAvailable, setCreditCustomerAvailable] = useState<
    boolean
  >(false);
  const [creditCustomerDetails, setCreditCustomerDetails] = useState<
    Array<object>
  >([]);
  const [validationErrorMessage, setValidationErrorMessage] = useState<string>(
    ""
  );
  const [color, setColor] = useState<string>("#d50000");
  const [creditCardAvailable, setCreditCardAvailable] = useState<boolean>(
    false
  );
  const [creditCardDetails, setCreditCardDetails] = useState<Array<object>>([]);
  const [arabicDetails, setArabicDetails] = useState<Array<object>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isBillListLoading, setBillListLoading] = useState<boolean>(false);
  const [billList, setBillList] = useState<IBillDetails[]>([]);

  useEffect(() => {
    setBillListLoading(true);
    let isMounted = true;
    async function getBillList() {
      const bills = await api.get("bills/bill-list");
      if (isMounted) {
        setBillList(bills);
        setBillListLoading(false);
      }
    }
    getBillList().catch((error) => {
      console.error(error);
      setBillListLoading(false);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const billNumberValidation = (bill_number: string) => {
    setDisplayBillDetails(false);
    const spaceValidation = /\s/;
    const numberValidation = /^[0-9]+$/;
    const isANumber = numberValidation.test(bill_number);
    if (bill_number !== "") {
      if (spaceValidation.test(bill_number)) {
        setDisplayBillDetails(false);
        setValidationError(true);
        setInvalidBillNumber(true);
        setColor("#d50000");
        setValidationErrorMessage("White spaces are not allowed");
      } else if (!isANumber) {
        setDisplayBillDetails(false);
        setValidationError(true);
        setInvalidBillNumber(true);
        setColor("#d50000");
        setValidationErrorMessage("Only numbers are allowed");
      } else {
        // if (bill_number.length === 17 || bill_number.length === 23) {
        setValidationError(false);
        setInvalidBillNumber(false);
        showBillDetails(bill_number, false);
        // } else {
        //   setValidationError(true);
        //   setInvalidBillNumber(true);
        //   setColor("#d50000");
        //   setValidationErrorMessage("Invalid Bill Number");
        //   setDisplayBillDetails(false);
        // }
      }
    } else {
      setValidationError(true);
      setInvalidBillNumber(true);
      setColor("#d50000");
      setValidationErrorMessage("Please enter a Bill Number");
    }
  };

  const showBillDetails = async (billNumber: string, inValid: boolean) => {
    setIsLoading(true);
    const invalidBillNumber = inValid;
    if (!invalidBillNumber) {
      try {
        const billDetails = await api.get(`bills/${billNumber}`);

        if (billDetails.length === 0) {
          setDisplayBillDetails(false);
          setValidationError(true);
          setValidationErrorMessage("No details for given bill number");
          setInvalidBillNumber(true);
          setIsLoading(false);
        } else {
          setIsLoading(false);
          setDisplayBillDetails(true);
          setValidationError(false);
          // }
          // if (billDetails.length > 0) {
          setBillDetails(billDetails);
          const creditcustomerDetails = await api.get(
            `bills/${billNumber}/creditcustomers`
          );

          if (creditcustomerDetails.length > 0) {
            setCreditCustomerDetails(creditcustomerDetails);
            setCreditCustomerAvailable(true);
          }
          const creditcardDetails = await api.get(
            `bills/${billNumber}/creditcards`
          );

          if (creditcardDetails.length > 0) {
            setCreditCardDetails(creditcardDetails);
            setCreditCardAvailable(true);
          }
          const arabicDetails = await api.get(`bills/arabic-description`);
          if (arabicDetails.length > 0) {
            setArabicDetails(arabicDetails);
          }
        }
      } catch (error) {
        console.log(error);
        throw error;
      }
    }
  };
  const onCloseSnackBar = () => {
    setValidationError(false);
  };

  const handleClearBillDetails = () => {
    setBillNumber("");
    setBillDetails([]);
    setCreditCustomerDetails([]);
    setCreditCustomerAvailable(false);
    setCreditCardDetails([]);
    setCreditCardAvailable(false);
    setArabicDetails([]);
    setDisplayBillDetails(false);
  };

  return (
    <>
      <Helmet>
        <title>Bills</title>
      </Helmet>
      <div className={classes.root}>
        <LoaderOverlay isLoading={isLoading} />
        <Modal
          aria-labelledby="bill-details-modal"
          className={classes.modal}
          open={displayBillDetails}
          onClose={(_event, reason) => {
            if (
              reason &&
              (reason === "backdropClick" || reason === "escapeKeyDown")
            )
              return;
            handleClearBillDetails();
          }}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
          }}
        >
          <Fade in={displayBillDetails}>
            <div className={classes.modalPaper}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginBottom: 10,
                }}
              >
                <IconButton
                  color="primary"
                  aria-label="close"
                  onClick={handleClearBillDetails}
                >
                  <CancelIcon />
                </IconButton>
              </div>
              <BillDetails
                billDetails={billDetails}
                creditCustomerDetails={creditCustomerDetails}
                creditCustomerAvailable={creditCustomerAvailable}
                creditCardDetails={creditCardDetails}
                creditCardAvailable={creditCardAvailable}
                invalidBillNumber={invalidBillNumber}
                arabicDetails={arabicDetails}
              />
            </div>
          </Fade>
        </Modal>
        <Paper elevation={3} className={classes.paper}>
          <Grid container spacing={3}>
            <Grid item></Grid>
            <Grid item xs={12}>
              {/* <form> */}
              <Formik
                enableReinitialize
                initialValues={{
                  bill_number: "",
                }}
                validationSchema={yup.object().shape({
                  bill_number: yup.string().required("Required"),
                })}
                onSubmit={async (values) => {
                  try {
                    billNumberValidation(values.bill_number);
                  } catch (error) {}
                }}
              >
                {({ values, handleChange }) => (
                  <Form>
                    <Grid
                      container
                      direction="row"
                      alignItems="center"
                      spacing={1}
                    >
                      <Grid item xs={6}>
                        <Grid container direction="column">
                          
                          <Typography style={{ textAlign: "left" }}>
                            Enter your Bill Number<span style={{color:"red"}}> * </span>
                          </Typography>
                          
                          <TextField
                            variant="outlined"
                            name="bill_number"
                            onChange={handleChange}
                            value={values.bill_number}
                          />
                          <ErrorMessage name="bill_number">
                            {(msg) => (
                              <div
                                className={classes.textFieldErrorMessageStyle}
                              >
                                {msg}
                              </div>
                            )}
                          </ErrorMessage>
                        </Grid>
                      </Grid>
                      <Grid
                        item
                        xs={3}
                        sm="auto" /* style={{ paddingTop: "2em" }} */
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          // onClick={() => billNumberValidation(billNumber)}
                          type="submit"
                          disabled={isLoading || isBillListLoading}
                        >
                          Show Bill Details
                        </Button>
                      </Grid>
                      {/* <Grid item xs={3} sm="auto" style={{ paddingTop: "2em" }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleClearBillDetails}
                        type="reset"
                      >
                        Clear
                      </Button>
                    </Grid> */}
                    </Grid>
                  </Form>
                )}
              </Formik>
              <Grid item xs={12}></Grid>
            </Grid>
            <Grid item xs={12} sm={12}>
              {isBillListLoading ? (
                <AppLoadingScreen />
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={12}>
                    <RecentBillsTable
                      data={billList}
                      billNumberValidation={billNumberValidation}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
            {validationError ? (
              <SnackBar
                onCloseError={onCloseSnackBar}
                status={validationErrorMessage}
                color={color}
              />
            ) : (
              ""
            )}
          </Grid>
        </Paper>
      </div>
    </>
  );
};
